import {
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import clsx from "clsx";
import { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";

import { Button, ButtonProps } from "./button";
import { Dropdown, DropdownPartProps } from "./dropdown";
import type { Item } from "./dropdown";

export type ContextMenuItemProps = Omit<
  DropdownPartProps,
  "rightIcon" | "value" | "onMouseEnter"
> & {
  subMenu?: DropdownPartProps[];
  keepOpen?: boolean;
  internal?: boolean;
};

type ContextMenuSharedProps = {
  id?: string;
  button: Omit<ButtonProps, "onClick">;
  buttonOverride?: ReactNode;
  onToggle?: (open: boolean) => void;
  className?: string;
  placement?: Placement;
  fallbackPlacements?: Placement[];
  offsetBy?: number;
};

export type ContextMenuProps = ContextMenuSharedProps &
  (
    | {
        variant?: "default";
        items: ContextMenuItemProps[];
      }
    | {
        variant: "grouped";
        items: { heading: string; items: ContextMenuItemProps[] }[];
      }
  );

type ContextMenuGroupedSection = {
  heading: string;
  items: ContextMenuItemProps[];
};

const isGroupedMenuItems = (
  value: unknown,
): value is ContextMenuGroupedSection[] => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (row) =>
      row !== null &&
      typeof row === "object" &&
      typeof (row as ContextMenuGroupedSection).heading === "string" &&
      Array.isArray((row as ContextMenuGroupedSection).items),
  );
};

type SubMenuState = {
  items: DropdownPartProps[];
  triggerIndex: number;
};

export const ContextMenu = (props: ContextMenuProps) => {
  const {
    id,
    className,
    button,
    buttonOverride,
    onToggle,
    placement = "bottom",
    fallbackPlacements,
    offsetBy = 0,
    items: itemsProp,
  } = props;
  const variant = props.variant ?? "default";
  const rawItems = itemsProp ?? [];

  const [menuOpen, setMenuOpen] = useState(false);
  const [subMenuState, setSubMenuState] = useState<SubMenuState | null>(null);
  const [showInternal, setShowInternal] = useState(false);
  const menu = useFloating({
    placement,
    open: menuOpen,
    onOpenChange: setMenuOpen,
    middleware: [
      shift({ padding: 10 }),
      flip({ fallbackPlacements: fallbackPlacements || ["bottom", "top"] }),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(0, availableHeight) - 10}px`,
          });
        },
      }),
      offset(offsetBy),
    ],
    whileElementsMounted: autoUpdate,
  });
  const subMenuFloating = useFloating({
    placement: "right-start",
    middleware: [offset(), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  const menuInteractions = useInteractions([
    useDismiss(menu.context),
    useClick(menu.context),
  ]);

  const subMenuRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (!menuOpen) {
      setSubMenuState(null);
      setShowInternal(false);
    }
    if (onToggle) onToggle(menuOpen);
  }, [menuOpen]);

  const toggleMenu = (event: MouseEvent) => {
    if (event.shiftKey && !showInternal) {
      setShowInternal(true);
      setMenuOpen((v) => !v);
      return undefined;
    }

    return setMenuOpen((v) => !v);
  };

  const includeItem = (i: ContextMenuItemProps) => {
    if (button.disabled) return showInternal && i.internal;
    return showInternal ? true : !i.internal;
  };

  let subMenuRefIndex = 0;
  const mapToDropdownItem = (item: ContextMenuItemProps): Item => {
    const index = subMenuRefIndex;
    subMenuRefIndex += 1;

    const clearSubMenuIfNoChildren = () => {
      if (!item.subMenu?.length) setSubMenuState(null);
    };

    const showSubMenuOnHover = () => {
      if (!item.subMenu?.length) {
        setSubMenuState(null);
        return;
      }
      setSubMenuState((prev) => {
        if (prev?.triggerIndex === index) return prev;
        return { items: item.subMenu!, triggerIndex: index };
      });
      subMenuFloating.refs.setReference(subMenuRefs.current[index]);
    };

    const toggleSubMenuOnClick = () => {
      if (!item.subMenu?.length) {
        setSubMenuState(null);
        return;
      }
      setSubMenuState((prev) => {
        if (prev?.triggerIndex === index) return null;
        return { items: item.subMenu!, triggerIndex: index };
      });
      subMenuFloating.refs.setReference(subMenuRefs.current[index]);
    };

    return {
      ...item,
      ref: (el) => {
        subMenuRefs.current[index] = el;
      },
      onClick: item.onClick
        ? (e) => {
            clearSubMenuIfNoChildren();
            item.onClick!(e);
            setMenuOpen(item.keepOpen || false);
          }
        : toggleSubMenuOnClick,
      onMouseEnter: item.subMenu?.length
        ? showSubMenuOnHover
        : () => setSubMenuState(null),
      rightIcon: item.subMenu?.length ? "solidChevronRight" : undefined,
    };
  };

  const groupedSections: ContextMenuGroupedSection[] =
    variant === "grouped" && isGroupedMenuItems(rawItems) ? rawItems : [];

  let flatItems: ContextMenuItemProps[] = [];
  if (variant === "grouped") {
    flatItems = [];
  } else {
    flatItems = Array.isArray(rawItems)
      ? (rawItems as ContextMenuItemProps[])
      : [];
  }

  let defaultDropdownItems: Item[] = [];
  let groupedDropdownItems: { heading: string; items: Item[] }[] = [];
  if (variant === "grouped") {
    groupedDropdownItems = groupedSections
      .map((group) => ({
        heading: group.heading,
        items: group.items.filter(includeItem).map(mapToDropdownItem),
      }))
      .filter((g) => g.items.length > 0);
  } else {
    defaultDropdownItems = flatItems.filter(includeItem).map(mapToDropdownItem);
  }

  return (
    <div
      className={clsx(className, button.disabled && "relative")}
      data-testid={`${id ? `${id}-` : ""}context-menu`}
      ref={menu.refs.setReference}
      {...menuInteractions.getReferenceProps()}
    >
      {button.disabled && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          className="absolute inset-0 cursor-not-allowed z-1"
          onClick={(e) => {
            e.stopPropagation();
            if (e.shiftKey) {
              setShowInternal(true);
              setMenuOpen((v) => !v);
            }
          }}
        />
      )}
      {buttonOverride ? (
        <button type="button" onClick={toggleMenu} disabled={button.disabled}>
          {buttonOverride}
        </button>
      ) : (
        <Button {...button} onClick={toggleMenu} />
      )}
      {menuOpen && (
        <div
          className="z-20 flex"
          ref={menu.refs.setFloating}
          style={menu.floatingStyles}
          {...menuInteractions.getFloatingProps()}
          data-testid={`${id ? `${id}-` : ""}context-menu-dropdown`}
        >
          {variant === "grouped" ? (
            <Dropdown
              variant="grouped"
              dropdownSize="full"
              items={groupedDropdownItems}
            />
          ) : (
            <Dropdown dropdownSize="full" items={defaultDropdownItems} />
          )}
        </div>
      )}
      {menuOpen && subMenuState?.items.length ? (
        <div
          className="z-20"
          ref={subMenuFloating.refs.setFloating}
          style={subMenuFloating.floatingStyles}
          data-testid={`${id ? `${id}-` : ""}context-menu-sub-menu-dropdown`}
        >
          <Dropdown
            className="min-w-[14rem]"
            items={subMenuState.items.map((subItem) => ({
              ...subItem,
              onClick: (e) => {
                subItem.onClick?.(e);
                setSubMenuState(null);
                setMenuOpen(false);
              },
            }))}
          />
        </div>
      ) : null}
    </div>
  );
};
